import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "../i18n";
import { Reveal, MaskedLine } from "../components/Motion";
import { usePageMeta } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  usePageMeta(t("meta.contact.t"), t("meta.contact.d"));

  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  const inputClass =
    "w-full border-b border-white/20 bg-transparent px-0 py-3 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-brand-yellow";

  const infoRows = [
    { icon: MapPin, label: t("contact.info.hq"), value: "Via Ca Nove 1/8, 37036 S. Martino Buon Albergo (VR), Italy", id: "address" },
    { icon: MapPin, label: t("contact.info.legal"), value: "Via Don Giovanni Bosco 8, 37060 Lugagnano di Sona (VR), Italy", id: "legal" },
    { icon: Mail, label: t("contact.info.write"), value: "info@gtechitalia.it", href: "mailto:info@gtechitalia.it", id: "email" },
    { icon: Phone, label: t("contact.info.call"), value: "045 451 4532", href: "tel:+390454514532", id: "phone" },
    { icon: Clock, label: t("contact.info.hours"), value: t("contact.info.hoursVal"), id: "hours" },
  ];

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-40 md:px-12 md:pt-56">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          {/* LEFT */}
          <div>
            <h1 data-testid="contact-title" className="font-display text-[clamp(2.4rem,5.5vw,5.5rem)] font-black leading-[0.95] tracking-tighter break-words">
              <MaskedLine delay={0.2}>{t("contact.title")}</MaskedLine>
              <MaskedLine delay={0.32}>
                <span className="text-brand-yellow">{t("contact.titleAccent")}</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p data-testid="contact-sub" className="mt-8 max-w-md text-base leading-relaxed text-neutral-400 md:text-lg">
                {t("contact.sub")}
              </p>
            </Reveal>

            <div className="mt-14 space-y-8">
              {infoRows.map((row, i) => (
                <Reveal key={row.id} delay={0.1 * i}>
                  <div data-testid={`contact-info-${row.id}`} className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-brand-line bg-brand-surface">
                      <row.icon className="h-5 w-5 text-brand-yellow" />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">{row.label}</p>
                      {row.href ? (
                        <a href={row.href} className="mt-1 block font-medium text-white transition-colors hover:text-brand-yellow">
                          {row.value}
                        </a>
                      ) : (
                        <p className="mt-1 font-medium text-white">{row.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* RIGHT: FORM */}
          <Reveal delay={0.3}>
            <div className="border border-brand-line bg-brand-surface p-8 md:p-12">
              {status === "success" ? (
                <div data-testid="contact-success-panel" className="flex min-h-[420px] flex-col items-center justify-center text-center">
                  <CheckCircle2 className="h-16 w-16 text-brand-yellow" strokeWidth={1.25} />
                  <h2 className="mt-6 font-display text-3xl font-bold tracking-tight">{t("contact.form.successTitle")}</h2>
                  <p className="mt-4 max-w-sm text-neutral-400">{t("contact.form.successBody")}</p>
                  <button
                    data-testid="contact-success-again-button"
                    onClick={() => { setForm({ name: "", email: "", company: "", service: "", message: "", website: "" }); setStatus("idle"); }}
                    className="mt-8 border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-brand-yellow hover:text-brand-yellow"
                  >
                    {t("contact.form.successAgain")}
                  </button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-8">
                  <div>
                    <label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t("contact.form.name")} *
                    </label>
                    <input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                  <div className="grid gap-8 md:grid-cols-2">
                    <div>
                      <label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">
                        {t("contact.form.email")} *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">
                        {t("contact.form.company")}
                      </label>
                      <input
                        id="company"
                        name="company"
                        data-testid="contact-company-input"
                        value={form.company}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="service" className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t("contact.form.service")} *
                    </label>
                    <select
                      id="service"
                      name="service"
                      data-testid="contact-service-select"
                      required
                      value={form.service}
                      onChange={handleChange}
                      className={`${inputClass} cursor-pointer appearance-none bg-brand-surface`}
                    >
                      <option value="" disabled>{t("contact.form.servicePlaceholder")}</option>
                      {t("contact.services").map((s, i) => (
                        <option key={i} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t("contact.form.message")} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      data-testid="contact-message-input"
                      required
                      minLength={10}
                      value={form.message}
                      onChange={handleChange}
                      className={`${inputClass} resize-none`}
                    />
                  </div>
                  {/* honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />
                  {status === "error" && (
                    <p data-testid="contact-error-alert" className="border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-400">
                      {t("contact.form.error")}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "sending"}
                    className="group flex w-full items-center justify-center gap-3 bg-brand-yellow px-8 py-5 font-display text-base font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300 disabled:opacity-60"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {t("contact.form.sending")}
                      </>
                    ) : (
                      <>
                        {t("contact.form.submit")}
                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="contact-map-section" className="border-t border-white/10">
        <iframe
          data-testid="contact-map"
          title="GTECH SRL — Via Ca Nove 1/8, 37036 San Martino Buon Albergo (VR)"
          src="https://www.google.com/maps?q=Via+Ca+Nove+1%2F8%2C+37036+San+Martino+Buon+Albergo+VR%2C+Italy&output=embed"
          className="h-[420px] w-full grayscale invert-[0.9] hue-rotate-180 contrast-[0.85]"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </section>
    </div>
  );
}
